import { type CandidateFilterState } from 'shared/hooks/candidateSearchUrlState'

interface FilterTabMeta {
    hasActive: boolean
    clearValues: Partial<CandidateFilterState>
}

export function getCandidateFilterTabMeta(filters: CandidateFilterState) {
    return {
        experience: {
            hasActive:
                filters.companyIds.length > 0 ||
                filters.positionTitles.length > 0 ||
                filters.industryNames.length > 0 ||
                filters.industryNamesNot.length > 0 ||
                filters.sectorNames.length > 0 ||
                filters.sectorNamesNot.length > 0 ||
                filters.locationText.length > 0 ||
                filters.analystClassYearGte !== undefined ||
                filters.analystClassYearLte !== undefined,
            clearValues: {
                companyIds: [],
                companyTimeFrame: undefined,
                companyRangeGte: undefined,
                companyRangeLte: undefined,
                companyAdvancedStartGte: undefined,
                companyAdvancedStartLte: undefined,
                companyAdvancedEndGte: undefined,
                companyAdvancedEndLte: undefined,
                positionTitles: [],
                positionTimeFrame: undefined,
                positionRangeGte: undefined,
                positionRangeLte: undefined,
                industryNames: [],
                industryNamesNot: [],
                industryTimeFrame: undefined,
                industryRangeGte: undefined,
                industryRangeLte: undefined,
                industryTopTierOnly: false,
                sectorNames: [],
                sectorNamesNot: [],
                sectorTimeFrame: undefined,
                sectorRangeGte: undefined,
                sectorRangeLte: undefined,
                sectorTopTierOnly: false,
                locationText: [],
                locationTextTimeFrame: undefined,
                locationTextRangeGte: undefined,
                locationTextRangeLte: undefined,
                analystClassYearGte: undefined,
                analystClassYearLte: undefined,
            },
        } satisfies FilterTabMeta,

        status: {
            hasActive:
                filters.recruitingStatus.length > 0 ||
                filters.outreachStatus.length > 0 ||
                filters.systemStatus.length > 0 ||
                filters.industryPrefFirst.length > 0 ||
                filters.industryPrefSecond.length > 0 ||
                filters.industryPrefThird.length > 0 ||
                filters.sectorPrefFirst.length > 0 ||
                filters.sectorPrefSecond.length > 0 ||
                filters.sectorPrefThird.length > 0 ||
                filters.locationPrefFirst.length > 0 ||
                filters.locationPrefSecond.length > 0 ||
                filters.locationPrefThird.length > 0 ||
                filters.prefStartDates.length > 0 ||
                filters.startDateGte !== undefined ||
                filters.startDateLte !== undefined,
            clearValues: {
                recruitingStatus: [],
                outreachStatus: [],
                systemStatus: [],
                industryPrefFirst: [],
                industryPrefSecond: [],
                industryPrefThird: [],
                sectorPrefFirst: [],
                sectorPrefSecond: [],
                sectorPrefThird: [],
                locationPrefFirst: [],
                locationPrefSecond: [],
                locationPrefThird: [],
                prefStartDates: [],
                startDateGte: undefined,
                startDateLte: undefined,
            },
        } satisfies FilterTabMeta,

        rating: {
            hasActive:
                filters.starRatingMin !== undefined ||
                filters.screeningNoteTemplateIds.length > 0 ||
                filters.screeningNoteStarRatingMin !== undefined ||
                filters.screeningNoteStarRatingMax !== undefined ||
                filters.remeetFilter !== undefined,
            clearValues: {
                starRatingMin: undefined,
                starRatingMax: undefined,
                screeningNoteTemplateIds: [],
                screeningNoteStarRatingMin: undefined,
                screeningNoteStarRatingMax: undefined,
                remeetFilter: undefined,
            },
        } satisfies FilterTabMeta,

        searches: {
            hasActive:
                filters.searchFilters.length > 0 ||
                filters.processStatusIds.length > 0 ||
                filters.processStatusGroupIds.length > 0,
            clearValues: {
                searchFilters: [],
                processStatusIds: [],
                processStatusGroupIds: [],
            },
        } satisfies FilterTabMeta,

        attributes: {
            hasActive:
                filters.genderIds.length > 0 ||
                filters.ethnicityIds.length > 0 ||
                filters.citizenshipIds.length > 0 ||
                filters.visaStatusIds.length > 0 ||
                filters.visaStatusIdsNot.length > 0 ||
                filters.qualitativeIds.length > 0 ||
                filters.qualitativeIdsNot.length > 0 ||
                filters.revenueExperienceIds.length > 0 ||
                filters.lgbtqIds.length > 0 ||
                filters.cpiPlacedIds.length > 0 ||
                filters.cpiPlacedIdsNot.length > 0 ||
                filters.referenceAttributeIds.length > 0 ||
                filters.mbaTierIds.length > 0 ||
                filters.hometownSearchTerms.length > 0 ||
                filters.includeDeactivated,
            clearValues: {
                genderIds: [],
                ethnicityIds: [],
                citizenshipIds: [],
                visaStatusIds: [],
                visaStatusIdsNot: [],
                qualitativeIds: [],
                qualitativeIdsNot: [],
                revenueExperienceIds: [],
                lgbtqIds: [],
                cpiPlacedIds: [],
                cpiPlacedIdsNot: [],
                referenceAttributeIds: [],
                mbaTierIds: [],
                hometownSearchTerms: [],
                includeDeactivated: false,
            },
        } satisfies FilterTabMeta,

        education: {
            hasActive:
                filters.undergradCollegeIds.length > 0 ||
                filters.mbaCollegeIds.length > 0 ||
                filters.majorIds.length > 0 ||
                filters.degreeTypeIds.length > 0 ||
                filters.degreeTypeIdsNot.length > 0 ||
                filters.gpaRangeMin !== undefined ||
                (filters.gpaNoGpaMode !== undefined &&
                    filters.gpaNoGpaMode !== 'include') ||
                filters.graduationRangeGte !== undefined ||
                filters.graduationRangeLte !== undefined ||
                filters.graduationEducationType !== undefined,
            clearValues: {
                undergradCollegeIds: [],
                mbaCollegeIds: [],
                majorIds: [],
                degreeTypeIds: [],
                degreeTypeIdsNot: [],
                gpaRangeMin: undefined,
                gpaRangeMax: undefined,
                gpaNoGpaMode: undefined,
                graduationRangeGte: undefined,
                graduationRangeLte: undefined,
                graduationEducationType: undefined,
            },
        } satisfies FilterTabMeta,
    }
}
